// Grout diagnostics. Flip to false to silence every line below; there are no other
// console statements in the grout path. Safe to leave enabled — it only reads the DOM
// and never affects a quantity, a price or a cart operation.
//
// This exists because the grout maths fails SILENTLY when a product's metafields are
// not the expected shape: the row simply takes the 0.17 fallback and looks like a
// deliberate answer. That is exactly how the `dimension`-type thickness defect survived
// review. See docs/HANDOVER-adhesive-grout-cart-widget.md §5.4.
const GROUT_DEBUG = true;

class AdhesiveAndGrout extends HTMLElement {
  constructor() {
    super();
    this.priceFormate = this.dataset.priceFormate; // e.g. '£{{amount}}'
    this.shopCurrency = this.dataset.shopCurrency;
    this.currentIso = this.dataset.currentIso;
    this.products = this.querySelectorAll('.main-Adhesive-text-button');
    this.totalPriceElbutton = this.querySelector('.Adhesive-btn-right.add-all'); // total price button
    this.totalPriceEl = this.querySelector('.total_price'); // total price element
    this.itemCountEl = this.querySelector('.Adhesive-items-count'); // "N items" in the footer
    this.init();
  }

  init() {
    // Quantity change. Repricing ONLY — a hand-typed quantity must survive its own
    // change event. See changeQuantity() / recalculateGroutQuantity() below.
    this.products.forEach((product) => {
      const qtyInput = product.querySelector('[data-quantity-input]');
      if (qtyInput) {
        qtyInput.addEventListener('change', (event) => {
          event.preventDefault();
          this.changeQuantity(product);
        });
      }

      // Inclusion toggle. This is the whole of the behaviour change: it flips `disabled`
      // on the row, which is already the single lever updateTotalPrice() and
      // addAllToCart() both filter on, and then refreshes the footer. It invents no
      // state of its own.
      //
      // Rows locked by Liquid (already in the cart, or unavailable) render their
      // checkbox `disabled`, so no event ever fires for them.
      const toggle = product.querySelector('.Adhesive-row-toggle input');
      if (toggle) {
        toggle.addEventListener('change', () => {
          if (toggle.checked) {
            product.removeAttribute('disabled');
          } else {
            product.setAttribute('disabled', '');
          }
          this.updateTotalPrice();
        });
      }

      // Variant change. The id is written onto the ROW, which is where buildCartItem()
      // reads it — it used to live on the per-row Add button that the toggle replaced.
      // Note there is deliberately no `&& someButton` guard here any more: pairing this
      // listener with an element that no longer exists would disable variant switching
      // silently, and the failure would only show as the wrong variant in a real order.
      const variantSelect = product.querySelector('.metaproduct-variants');
      if (variantSelect) {
        variantSelect.addEventListener('change', () => {
          product.dataset.variantId = variantSelect.value;

          // The price shown, and the price changeQuantity() reprices from, are both
          // baked into the HTML for whichever variant loaded first (see
          // snippets/meta-products.liquid) and are never touched again on their own.
          // Switching colour therefore left the old variant's price on screen even
          // though the correct variant was added to cart. Each <option> now carries
          // its own price, so pull the selected one across before repricing.
          const qtyInput = product.querySelector('[data-quantity-input]');
          const selectedOption = variantSelect.selectedOptions[0];
          if (qtyInput && selectedOption) {
            qtyInput.dataset.price = selectedOption.dataset.price || '0';
            if (selectedOption.dataset.comparePrice) {
              qtyInput.dataset.comparePrice = selectedOption.dataset.comparePrice;
            } else {
              delete qtyInput.dataset.comparePrice;
            }
            this.changeQuantity(product);
          }
        });
      }

      if (product.dataset.joint) {
        const jointOptions = product.querySelector('.joint-options');
        if (jointOptions) {
          jointOptions.addEventListener('change', (event) => {
            const selectedJoint = event.target.value;
            product.dataset.joint = selectedJoint;
            // A joint change is a new calculation input, so it DOES overwrite the
            // quantity — that is the one path allowed to. Then reprice off the new
            // number. A manual quantity edit takes only the second half of this.
            this.recalculateGroutQuantity(product);
            this.changeQuantity(product);
            this.groutDebug(product, 'joint change');
          });
        }
      }

      // First paint. Logged for every grout row, including the ones that fell back —
      // those are the rows worth knowing about.
      this.groutDebug(product, 'load');
    });

    // "No Thanks" button
    const NoThanks = this.querySelector('.Adhesive-btn-left.no-thanks');
    if (NoThanks) {
      NoThanks.addEventListener('click', (event) => {
        event.preventDefault();
        this.hide();
      });
    }

    // ✅ "Add All" button — NEW FUNCTIONALITY
    if (this.totalPriceElbutton) {
      this.totalPriceElbutton.addEventListener('click', (event) => {
        event.preventDefault();
        this.addAllToCart();
      });
    }

    // Initial total calculation
    this.updateTotalPrice();
  }

  // One row -> one /cart/add.js line item. `product` is a `.main-Adhesive-text-button`,
  // i.e. exactly one metaproduct row, so the quantity read here is always the clicked
  // row's own (§3.8, bug #7 — the old code scoped to `.Adhesive-products`, the wrapper
  // around EVERY row under a heading, and so always returned the first row's quantity).
  //
  // The variant id comes off the row rather than off an Add button: the per-row Add
  // buttons were replaced by inclusion toggles, and the variant <select> now writes its
  // id back to the same place this reads it from.
  buildCartItem(product) {
    const qtyInput = product.querySelector('[data-quantity-input]');
    if (!qtyInput) return null;

    return {
      id: product.dataset.variantId,
      quantity: parseInt(qtyInput.value) || 1,
      properties: {
        "Parent id": this.dataset.prentId,
      },
    };
  }

  // ✅ Add All Function
  addAllToCart() {
    const allProducts = Array.from(this.products);

    // Filter only active (non-disabled) product blocks. `disabled` is what the row's
    // toggle writes, so a row the customer switched off drops out here — same lever,
    // same filter as updateTotalPrice(), untouched by the restyle.
    const activeProducts = allProducts.filter((product) => {
      const mainBtn = product.closest('.main-Adhesive-text-button');
      return mainBtn && !mainBtn.hasAttribute('disabled');
    });

    // Collect all active product items
    const items = activeProducts.map((product) => this.buildCartItem(product)).filter(Boolean);

    if (items.length === 0) return;

    const data = { items };

    // Single bulk request
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((response) => {
        console.log('All products added:', response);

        // Trigger cart refresh
        document.dispatchEvent(new CustomEvent("cart:refresh", {
          detail: { scrollToTop: false },
        }));

        // Feedback for button
        this.totalPriceElbutton.textContent = 'All Added!';
        this.totalPriceElbutton.disabled = true;
        setTimeout(() => {
          this.totalPriceElbutton.textContent = 'Add to basket';
          this.totalPriceElbutton.disabled = false;
        }, 2000);
      })
      .catch((error) => {
        console.error('Error adding all products:', error);
      });
  }

  hide() {
    this.parentElement.classList.add('hidden');
  }

  // One console group per grout row. Called on first paint for every row, and again
  // after each joint change so the recomputed rate and quantity are visible.
  //
  // `when` is 'load' or 'joint change'. Adhesive rows carry no data-grout-mode and are
  // skipped — this is the grout path only.
  groutDebug(product, when) {
    if (!GROUT_DEBUG) return;

    const mode = product.dataset.groutMode;
    if (!mode) return;

    const d = product.dataset;
    const qtyInput = product.querySelector('[data-quantity-input]');
    const qty = qtyInput ? qtyInput.value : '(no input)';
    const fallbackActive = mode === 'fallback';
    const reason = fallbackActive ? ` — reason: ${d.groutFallbackReason || 'unknown'}` : '';

    console.groupCollapsed(
      `[Grout] ${d.debugTitle || '(untitled)'} — ${mode.toUpperCase()}` +
        ` (fallback ${fallbackActive ? 'ACTIVE' : 'not active'}${reason}) — ${when}`
    );

    if (mode === 'formula') {
      console.log(
        `inputs   : ${d.tileWCm}cm × ${d.tileLCm}cm · ${d.thicknessMm}mm thick · ${d.joint}mm joint`
      );
    } else {
      console.log(`inputs   : none — ${mode} is a flat rate, joint width has no effect`);
    }

    console.log(`rate     : ${d.groutRate} bags/m²`);
    console.log(`area     : ${d.sqm} m²   →   qty ${qty}`);

    // What Shopify actually returned, as opposed to what the theme inferred. `.type` is
    // authoritative; the JSON shows the value's shape. Both come from the diagnostic
    // block in snippets/meta-products.liquid and can be removed with it.
    // A unit other than MILLIMETRES is the one silent-wrong-answer case left in the
    // grout path: every value is treated as mm by instruction, so a centimetre value is
    // read 10x low with no error shown anywhere. Called out loudly for that reason.
    // Shopify returns the SHORT form here — "mm", not "MILLIMETERS". Both are accepted;
    // an earlier version tested only for the long form and warned on correct data.
    const unit = d.debugThicknessUnit || '';
    const unitWarn = unit && !/^(mm|milli)/i.test(unit) ? '   ⚠ NOT MILLIMETRES — read 10x low' : '';

    console.log(
      `metafields:\n` +
        `  product.type    → ${JSON.stringify(d.debugProductType)}\n` +
        `  thickness .type → ${JSON.stringify(d.debugThicknessType)}\n` +
        `  thickness .value→ ${d.debugThicknessValue} ${unit}${unitWarn}\n` +
        `  thickness .raw  → ${d.debugThicknessRaw}\n` +
        `  dimensions.type → ${JSON.stringify(d.debugDimsType)}\n` +
        `  dimensions.value→ ${d.debugDimsJson}`
    );

    console.groupEnd();
  }

  // Recompute the grout quantity from the current inputs and WRITE it into the box.
  //
  // Deliberately separate from changeQuantity(). This is the only code allowed to
  // overwrite a quantity the customer can see, and only the joint-width listener calls
  // it. Previously the recompute lived inside changeQuantity(), which the quantity
  // input's own `change` listener also calls — so typing 8 recalculated the formula and
  // replaced the 8 before the customer's finger left the key (§3.8, bug #2). Splitting
  // the two gives the required behaviour: a joint change is a new calculation input and
  // overwrites; a manual edit persists until an input actually changes.
  //
  // Grout, formula mode ONLY. Mosaic (0.7 bags/m²) and fallback (0.17 bags/m²) are flat
  // rates that do not move with the joint, and their tile attributes are deliberately not
  // emitted — so this must not run for them. Math.ceil(NaN) is NaN, which would reach the
  // customer as the literal string "NaN" in the quantity box.
  //
  // Every number below arrives from the Liquid on a data- attribute. There is no numeric
  // constant here — not the 833/10000 factor, not the flat rates, not even the default
  // joint width. Two independent copies of those constants silently diverging on first
  // interaction is the bug this rebuild exists to remove. See
  // snippets/meta-products.liquid and docs/PROPOSAL-adhesive-grout-cart-widget.md §3.6.
  recalculateGroutQuantity(product) {
    if (product.dataset.groutMode !== 'formula') return;

    const qtyInput = product.querySelector('[data-quantity-input]');
    if (!qtyInput) return;

    const jointMm = parseFloat(product.dataset.joint);
    const widthCm = parseFloat(product.dataset.tileWCm);
    const lengthCm = parseFloat(product.dataset.tileLCm);
    const thicknessMm = parseFloat(product.dataset.thicknessMm);
    const groutFactor = parseFloat(product.dataset.groutFactor);
    const totalArea = parseFloat(product.dataset.sqm) || 0;

    // Same precondition as the Liquid guard, and the same reason: parseFloat of an
    // absent or unparseable attribute is NaN, and NaN fails every comparison here.
    if (!(jointMm > 0 && widthCm > 0 && lengthCm > 0 && thicknessMm > 0 && groutFactor > 0)) {
      return;
    }

    // Same operation order as the Liquid — multiply first, divide once, round only at
    // the ceil — so changing the joint and changing it back returns the number to
    // exactly its rendered value.
    const numerator = (widthCm + lengthCm) * thicknessMm * jointMm * groutFactor;
    const denominator = widthCm * lengthCm;
    const groutRate = numerator / denominator;

    let groutBagsQty = Math.ceil(totalArea * groutRate);
    if (groutBagsQty < 1) groutBagsQty = 1;

    qtyInput.value = groutBagsQty;

    // Keep data-grout-rate in step with the quantity just written. Nothing in the
    // widget reads it back — the recalculation above derives everything from the
    // tile attributes, never from this — so a stale value changed no customer-facing
    // number. It did make the GROUT_DEBUG output self-contradictory after every joint
    // change: the rate stayed at whatever Liquid rendered for the 3mm default while
    // the quantity moved, so `rate x area` no longer equalled the quantity shown.
    // That is a real cost, because the diagnostic exists to catch silent wrongness and
    // this made the diagnostic itself silently wrong. Also stops a future consumer of
    // the attribute inheriting a value that is only correct on first paint.
    product.dataset.groutRate = groutRate;
  }

  // Reprice a row from whatever quantity is currently in its box, and refresh the
  // running total. Never changes the quantity itself.
  changeQuantity(product) {
    const priceFormat = this.priceFormate;
    const qtyInput = product.querySelector('[data-quantity-input]');
    if (!qtyInput) return;

    const priceEl = product.querySelector('[data-product-price]');
    const comparePriceEl = product.querySelector('[data-product-compare-price]');

    const qty = parseInt(qtyInput.value) || 0;
    const price = parseInt(qtyInput.dataset.price) || 0;
    const comparePrice = parseInt(qtyInput.dataset.comparePrice) || 0;

    // ✅ Base price calculations
    const totalPrice = (price * qty) / 100;
    const totalComparePrice = (comparePrice * qty) / 100;

    const formattedPrice = priceFormat.replace('{{amount}}', totalPrice.toFixed(2));
    const formattedComparePrice = priceFormat.replace('{{amount}}', totalComparePrice.toFixed(2));

    if (priceEl) priceEl.textContent = formattedPrice;
    if (comparePriceEl) comparePriceEl.textContent = comparePrice > 0 ? formattedComparePrice : '';

    // ✅ Update global total price display
    this.updateTotalPrice();
  }


  updateTotalPrice() {
    const priceFormat = this.priceFormate;
    let total = 0;
    let hasQty = false;

    const allProducts = Array.from(this.products);
    const activeProducts = allProducts.filter((product) => {
      const mainBtn = product.closest('.main-Adhesive-text-button');
      return mainBtn && !mainBtn.hasAttribute('disabled');
    });

    activeProducts.forEach((product) => {
      const qtyInput = product.querySelector('[data-quantity-input]');
      if (!qtyInput) return;

      const qty = parseInt(qtyInput.value) || 0;
      const price = parseInt(qtyInput.dataset.price) || 0;

      if (qty > 0) hasQty = true;
      total += (price * qty) / 100;
    });

    if (this.totalPriceEl) {
      const formattedTotal = priceFormat.replace('{{amount}}', total.toFixed(2));
      this.totalPriceEl.textContent = formattedTotal;
      this.totalPriceEl.classList.toggle('disabled', !hasQty);
    }

    // "N items" counts the rows the customer has left switched ON, not every row that
    // was rendered.
    if (this.itemCountEl) {
      const count = activeProducts.length;
      this.itemCountEl.textContent = `${count} item${count === 1 ? '' : 's'}`;
    }

    if (this.totalPriceElbutton) {
      this.totalPriceElbutton.disabled = activeProducts.length === 0 || !hasQty;

      // ✅ Toggle visibility of adhesive-and-grout element
      //
      // Keyed to `data-locked`, NOT to "no active rows". Locked means Liquid rendered
      // the row unusable — already in the cart, or unavailable — which is the case this
      // hide was written for, and it cannot be undone from the page. Switching every
      // toggle off is a different thing entirely: it is reversible, so hiding the box
      // on it would strand the customer with no way to get it back.
      const allLocked =
        allProducts.length > 0 && allProducts.every((product) => product.hasAttribute('data-locked'));

      if (allLocked) {
        this.parentElement.classList.add('hidden');
      } else {
        this.parentElement.classList.remove('hidden');
      }
    }
  }
}

customElements.define('adhesive-and-grout', AdhesiveAndGrout);
